import { useGetCalculateRegistryPurchaseInfoQuery } from '@graphql/queries/registry.graphql.generated';
import type { StripeElementsProviderLazyConfig } from '@shared/core/stripe';
import { useStripeElementsContext } from '@shared/core/stripe';
import React, { useEffect, useMemo } from 'react';

type LoadingRegistryCheckoutSettingsProps = {
  eventId: string | undefined;
  /** Content to display upon success */
  children: React.ReactNode;
  /** Content to display upon unrecoverable failure */
  fallbackContent?: React.ReactNode;
};

export const LoadingRegistryCheckoutSettings: React.FC<LoadingRegistryCheckoutSettingsProps> = ({ children, eventId, fallbackContent }) => {
  const { data, error, loading } = useGetCalculateRegistryPurchaseInfoQuery({
    variables: {
      payload: {
        eventId: eventId ?? '',
        // this is a known mis-use of the endpoint;
        //   it is justified in the plan document 'doc/plans/NREG-3042-stripe-key-mulfiple-platform-accounts.md'
        lineItems: [],
        address: null,
      },
    },
    skip: !eventId,
    batchMode: 'fast',
  });
  const calculateRegistryPurchaseInfo = data?.calculateRegistryPurchaseInfo;
  const lazyConfig = useMemo<StripeElementsProviderLazyConfig | null>(
    () => (!(error || loading) ? (calculateRegistryPurchaseInfo?.checkout ?? null) : null),
    [calculateRegistryPurchaseInfo, error, loading],
  );

  const { setLazyConfig } = useStripeElementsContext();
  useEffect(() => {
    if (lazyConfig) {
      setLazyConfig(lazyConfig);
    }
  }, [lazyConfig, setLazyConfig]);

  return useMemo(() => {
    if (error && fallbackContent) {
      // we have reached a final error state
      return <>{fallbackContent}</>;
    }
    if (lazyConfig) {
      // we have received our critical configuration
      return <>{children}</>;
    }
    // until we are configured, we will not render anything
    return null;
  }, [error, lazyConfig, children, fallbackContent]);
};
